"use client"

import React, { useEffect, useMemo, useState } from "react"
import Link from "next/link"
import { useSearchParams } from "next/navigation"
import { loadOrderLineItemsAction, loadOrders } from "@/actions/orderActions"
import { AddressROV1, OrderCtnrROV1 } from "@ub/order-client"
import { SearchIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { formatDate, formatPrice } from "@/lib/helper"
import { generateProductLink } from "@/lib/link-utils/link-utils"
import { getSbLocaleFromIsoLocale } from "@/lib/locale-helper"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { OrderSortKey, OrdersResult } from "@/lib/storefront/account/order-service"
import { OrderLineItemData } from "@/lib/storefront/order/order"
import {
  Button,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/components"
import { DataPagination } from "@/components/ui/components/data-pagination"
import ImageWithBadge from "@/components/ui/components/image-with-badge"
import ProductCardAddToBasket from "@/components/product/ProductCardAddToBasket"

interface AccountOrderListClientsProps {
  accountContext: AccountContext
  initialOrders: OrdersResult
}

const sortByDateAsc = (a, b) => {
  return (parseInt(a?.creationDate?.toString() ?? "0") ?? 0) - (parseInt(b?.creationDate?.toString() ?? "0") ?? 0)
}

const sortByDateDesc = (a, b) => {
  return (parseInt(b?.creationDate?.toString() ?? "0") ?? 0) - (parseInt(a?.creationDate?.toString() ?? "0") ?? 0)
}

const sortByPriceAsc = (a, b) => {
  return a?.payments[0]?.paymentTotalAmount?.value - b?.payments[0]?.paymentTotalAmount?.value
}

const sortByPriceDesc = (a, b) => {
  return b?.payments[0]?.paymentTotalAmount?.value - a?.payments[0]?.paymentTotalAmount?.value
}

function AccountOrderLineItemCard(props: { orderLineItemData: OrderLineItemData; isoLocale: string; isB2B: boolean }) {
  const searchParams = useSearchParams()
  const t = useTranslations("storefront")
  const variantLink = generateProductLink(
    props.orderLineItemData.product.title,
    props.orderLineItemData.product.id,
    searchParams,
    getSbLocaleFromIsoLocale(props.isoLocale)
  )

  return (
    <div className={"w-full"}>
      {/*
<p className={"text-xs"}>{JSON.stringify(props.orderLineItemData.orderLineItem,undefined,2)}</p>
*/}

      <div className={"min-h-24"}>
        <div className={"flex flex-row flex-wrap gap-2"}>
          <ImageWithBadge
            className={"aspect-square size-20 object-contain"}
            src={props.orderLineItemData.product.image ?? ""}
            width={80}
            height={80}
            badgeText={props.orderLineItemData.orderLineItem.quantity.value}
            alt={props.orderLineItemData.product.title}
          />
          <div className={"flex flex-col items-start justify-start gap-2"}>
            <div className={"flex flex-col items-start justify-start gap-1"}>
              <p className={"font-bold"}>{props.orderLineItemData.product.title}</p>
              <p className={" text-sm"}>{props.orderLineItemData.product.description}</p>
            </div>

            <div className={"flex flex-row gap-2 items-center"}>
              <Link prefetch={false} href={variantLink}>
                <Button size={"smNoGrow"} className="w-full" variant={"default"}>
                  {t("product.go_to_product")}
                </Button>
              </Link>

              <ProductCardAddToBasket
                isoLocale={props.isoLocale}
                isB2BCustomer={props.isB2B}
                articleIdToAdd={props.orderLineItemData.product.id}
                productData={props.orderLineItemData.product}
                prices={props.orderLineItemData.product.effectivePrices}
                quantity={props.orderLineItemData.product?.stepOrderQuantity ?? 1}
                showSmall={true}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function AccountOrderCard(props: { order: OrderCtnrROV1; isoLocale: string; isB2B: boolean }) {
  const [lineItemsWithProduct, setLineItemsWithProduct] = useState<OrderLineItemData[]>([])
  const addressShortName = useMemo(() => {
    if (props?.order?.included?.commonShipToAddress) {
      const firstAddress: AddressROV1 = Object.values(props.order.included.commonShipToAddress ?? {})[0]
      const firstName = firstAddress?.firstName ?? ""
      const lastName = firstAddress?.lastName ?? ""
      return (firstName + " " + lastName).trim()
    }
  }, [props.order])

  //Lazy load order line items
  useEffect(() => {
    loadOrderLineItemsAction(props.order).then((orderLineItems) => {
      if (orderLineItems) {
        setLineItemsWithProduct(orderLineItems)
      }
    })
  }, [props.order])

  return (
    <div className={"flex w-full flex-col gap-2 bg-white"}>
      <div className={"bg-ub-background flex w-full flex-row flex-wrap justify-between gap-2 p-2"}>
        <div className={"flex flex-col gap-2"}>
          <p className={"font-bold"}>Bestelldatum</p>
          <p>{formatDate(props.isoLocale, props.order?.data?.creationDate)}</p>
        </div>
        <div className={"flex flex-col gap-2"}>
          <p className={"font-bold"}>Status</p>
          <p>{props.order?.data?.status}</p>
        </div>
        <div className={"flex flex-col gap-2"}>
          <p className={"font-bold"}>Bestellnummer</p>
          <p>
            <Link
              prefetch={false}
              className={"cursor-pointer text-blue-600 underline"}
              href={`/account/order/${props.order?.data?.id}`}
            >
              {props.order?.data?.documentNumber}
            </Link>
          </p>
        </div>

        <div className={"flex flex-col gap-2"}>
          <p className={"font-bold"}>Versandadresse</p>
          <p>{addressShortName}</p>
        </div>
        <div className={"flex flex-col gap-2"}>
          <p className={"font-bold"}>Gesamtsumme</p>
          <p>
            {formatPrice(
              props.isoLocale,
              props.order?.data?.totals?.grandTotal?.gross?.value,
              props.order?.data?.totals?.grandTotal?.gross?.currency
            )}
          </p>
        </div>
      </div>

      <div className={"flex flex-col gap-2 p-2"}>
        {lineItemsWithProduct && (
          <>
            {lineItemsWithProduct.map((orderLineItemData) => (
              <div key={orderLineItemData.orderLineItem.id}>
                <AccountOrderLineItemCard
                  isoLocale={props.isoLocale}
                  isB2B={props.isB2B}
                  orderLineItemData={orderLineItemData}
                />
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  )
}

export default function AccountOrderListClient(props: AccountOrderListClientsProps) {
  const accountContext: AccountContext = props.accountContext
  const t = useTranslations("storefront")

  const [selectedSort, setSelectedSort] = React.useState<OrderSortKey>("SORT_BY_DATE_DESC")

  const [selectedFilterYear, setSelectedFilterYear] = React.useState<string>("-")
  const [selectedSearchText, setSelectedSearchText] = React.useState<string>("")

  const [orders, setOrders] = React.useState<OrdersResult>(props.initialOrders)

  const years: string[] = useMemo(() => {
    return ["-", "2025", "2024", "2023", "2022", "2021", "2020", "2019", "2018"] //ToDO: Fill with current year until 2018
  }, [])

  function handleSelectSort(currentSort: OrderSortKey) {
    setSelectedSort(currentSort)
  }

  function handleSelectFilter(currentFilter: string) {
    setSelectedFilterYear(currentFilter)
  }

  const getPagedOrders = async (page) => {
    console.log("page changed to ", page)
    setOrders(
      await loadOrders({
        pageSize: 10,
        pageNumber: page - 1,
        sortKey: selectedSort,
        filterYear: selectedFilterYear,
        filterText: selectedSearchText,
      })
    )
  }

  useEffect(() => {
    const fetch = async () => {
      await getPagedOrders(1)
    }

    fetch()
  }, [selectedSort, selectedFilterYear, selectedSearchText])

  return (
    <>
      <div className={"flex flex-col gap-4"}>
        <div className="bg-gray-100 p-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            {/* Left Section */}
            <div className="flex flex-col gap-2">
              <p className="text-sm text-gray-600">Hier finden Sie Ihre Bestellungen.</p>

              <div className={"flex flex-row items-center"}>
                <Select onValueChange={handleSelectSort} value={selectedSort}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {["SORT_BY_DATE_ASC", "SORT_BY_DATE_DESC", "SORT_BY_PRICE_ASC", "SORT_BY_PRICE_DESC"].map(
                        (currentSort: any) => (
                          <SelectItem
                            value={currentSort}
                            key={currentSort}
                            onClick={() => handleSelectSort(currentSort)}
                          >
                            {t("sort_order." + currentSort)}
                          </SelectItem>
                        )
                      )}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Right Section */}
            <div className="flex flex-col gap-2">
              {/* Search box with icon */}
              <div className="relative w-full pr-2 sm:w-64 sm:pr-0">
                <SearchIcon
                  strokeWidth={2}
                  className="pointer-events-none absolute right-1 top-1/2 size-5 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Alle Bestellungen durchsuchen"
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      setSelectedSearchText(event.currentTarget.value)
                    }
                  }}
                  className="w-[103%] rounded-md border border-gray-300 py-2 pl-4 pr-10 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Text + filter */}
              <div className="flex items-center justify-between gap-2 text-sm sm:justify-start">
                <span>
                  <strong>{orders.filteredSize} Bestellungen</strong> aufgegeben in
                </span>
                <div className={"flex flex-row items-center justify-end gap-2"}>
                  <Select onValueChange={handleSelectFilter} value={selectedFilterYear}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {years.map((currentFilter: string) => (
                          <SelectItem
                            value={currentFilter}
                            key={currentFilter}
                            onClick={() => handleSelectFilter(currentFilter)}
                          >
                            {currentFilter}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>{orders.orders.length} Bestellungen</div>

        <div className={"flex flex-col items-end gap-2"}>
          {orders.orders?.map((order) => (
            <div
              key={order?.data?.documentNumber}
              className={"flex w-full flex-col gap-1 rounded-sm border border-gray-200 p-0"}
            >
              <AccountOrderCard
                order={order}
                isB2B={props.accountContext.isB2B}
                isoLocale={props.accountContext.isoLocale}
              />
            </div>
          ))}
        </div>
        <DataPagination
          initialPage={1}
          totalPages={Math.ceil(orders.filteredSize / 10)}
          onPageChange={(page) => getPagedOrders(page)}
        ></DataPagination>
      </div>
    </>
  )
}
